import type { ExpoChipState, KdsFlowDetail, KdsTaskDetail, KdsTaskDetailStatus } from '@ordr-as/ordr-service-types/epos';
import { ExpoChipState as ExpoChipStateValues } from '@ordr-as/ordr-service-types/epos';
import type { ExpoDisplayItem, FlowGraph, ItemTotal } from './types';

export const RECENTLY_DONE_CAP = 20;

function computeDepths(flowStationIds: number[], upstreamsByFlowStationId: Map<number, number[]>): Map<number, number> {
  // Longest path from a flow's start; flows are tiny, so relax until stable.
  const depth = new Map<number, number>(flowStationIds.map((id) => [id, 0]));
  for (let pass = 0; pass < flowStationIds.length; pass++) {
    let changed = false;
    for (const id of flowStationIds) {
      for (const upstream of upstreamsByFlowStationId.get(id) ?? []) {
        const candidate = (depth.get(upstream) ?? 0) + 1;
        if (candidate > (depth.get(id) ?? 0)) {
          depth.set(id, candidate);
          changed = true;
        }
      }
    }
    if (!changed) break;
  }
  return depth;
}

export function buildFlowGraph(flowDetails: KdsFlowDetail[]): FlowGraph {
  // The only thing the client still derives from the flows is each flow station's
  // depth, used to order an order line's progress chips in the station detail sheet.
  // (A task's station now comes straight off task.stationId, and "waiting for" uses
  // task.dependencyTaskIds — neither needs the flow graph anymore.)
  const flowStationIds: number[] = [];
  const upstreamsByFlowStationId = new Map<number, number[]>();

  for (const flow of flowDetails) {
    for (const flowStation of flow.stations ?? []) {
      if (flowStation.id !== undefined) flowStationIds.push(flowStation.id);
    }
    for (const link of flow.links ?? []) {
      if (link.upstreamFlowStationId === undefined || link.downstreamFlowStationId === undefined) continue;
      const upstreams = upstreamsByFlowStationId.get(link.downstreamFlowStationId) ?? [];
      upstreams.push(link.upstreamFlowStationId);
      upstreamsByFlowStationId.set(link.downstreamFlowStationId, upstreams);
    }
  }

  return { depthByFlowStationId: computeDepths(flowStationIds, upstreamsByFlowStationId) };
}

/** Statuses that keep a task on the board. RECALLED came back from done and needs attention again. */
export function isActiveStatus(status: KdsTaskDetailStatus | undefined): boolean {
  return status === 'PENDING' || status === 'QUEUED' || status === 'ONGOING' || status === 'RECALLED';
}

/** RECALLED renders like ONGOING: it is back in the kitchen's hands. */
export function isOngoingStatus(status: KdsTaskDetailStatus | undefined): boolean {
  return status === 'ONGOING' || status === 'RECALLED';
}

function isUnfinishedStatus(status: KdsTaskDetailStatus | undefined): boolean {
  return status !== 'DONE' && status !== 'VOIDED';
}

export function selectStationTasks(tasks: KdsTaskDetail[], stationId: number): KdsTaskDetail[] {
  return tasks.filter((task) => isActiveStatus(task.status) && task.stationId === stationId);
}

function boardRank(status: KdsTaskDetailStatus | undefined): number {
  if (isOngoingStatus(status)) return 0;
  if (status === 'QUEUED') return 1;
  return 2;
}

function createdAtMs(task: KdsTaskDetail): number {
  // Tasks without a timestamp sort last within their rank.
  return task.createdUtc ? new Date(task.createdUtc).getTime() : Number.MAX_SAFE_INTEGER;
}

export function sortBoardTasks(tasks: KdsTaskDetail[]): KdsTaskDetail[] {
  return [...tasks].sort((a, b) => boardRank(a.status) - boardRank(b.status) || createdAtMs(a) - createdAtMs(b));
}

/**
 * The station a PENDING task is waiting on: the immediate upstream task (a direct
 * dependency, same order by construction) that hasn't finished yet. The backend
 * resolves dependencies per task, so we just read task.dependencyTaskIds. When more
 * than one direct upstream is unfinished, the lowest station id wins for stability.
 */
export function resolveWaitingFor(task: KdsTaskDetail, allTasks: KdsTaskDetail[]): number | undefined {
  if (task.dependencyTaskIds.length === 0) return undefined;

  const byId = new Map(allTasks.map((t) => [t.id, t]));
  const blockers = task.dependencyTaskIds
    .map((id) => byId.get(id))
    .filter((t): t is KdsTaskDetail => t !== undefined && isUnfinishedStatus(t.status));

  if (blockers.length === 0) return undefined;
  return blockers.sort((a, b) => a.stationId - b.stationId)[0]?.stationId;
}

function updatedAtMs(task: KdsTaskDetail): number {
  const stamp = task.updatedUtc ?? task.createdUtc;
  return stamp ? new Date(stamp).getTime() : 0;
}

function chipState(status: KdsTaskDetailStatus | undefined): ExpoChipState {
  if (status === 'DONE') return ExpoChipStateValues.DONE;
  if (isOngoingStatus(status)) return ExpoChipStateValues.ONGOING;
  return ExpoChipStateValues.TODO;
}

/**
 * Local input type for selectExpoDisplayItems: accepted by station detail which
 * has KdsTaskDetail tasks but not the generated ExpoOrder (which has no tasks field).
 */
type OrderWithTasks = {
  tasks: KdsTaskDetail[];
};

/**
 * Builds an Expo card's order lines: the same order line appears in one task
 * per station of its route, so group by orderLineId and turn each task into a
 * progress chip, ordered by the flow's station sequence.
 * Used by the station detail sheet (station path still derives display items client-side).
 */
export function selectExpoDisplayItems(order: OrderWithTasks, graph: FlowGraph): ExpoDisplayItem[] {
  const byLine = new Map<string, ExpoDisplayItem>();

  for (const task of order.tasks) {
    for (const item of task.items ?? []) {
      const key = item.orderLineId !== undefined ? String(item.orderLineId) : `${task.id}-${item.id}`;
      const line = byLine.get(key) ?? {
        key,
        name: item.itemName ?? 'Unknown item',
        quantity: item.quantity ?? 1,
        modifiers: [],
        comment: undefined,
        chips: [],
      };
      // The same line repeats in one task per station; merge details across copies.
      const modifiers = (item.modifiers ?? []).map((m) => m.itemName ?? '').filter(Boolean);
      line.modifiers = [...new Set([...line.modifiers, ...modifiers])];
      line.comment = line.comment ?? item.comment;
      line.chips.push({ taskId: task.id ?? 0, stationId: task.stationId ?? 0, state: chipState(task.status) });
      byLine.set(key, line);
    }
  }

  const depthOf = (taskId: number, fallback: number) => {
    const task = order.tasks.find((t) => t.id === taskId);
    if (task?.flowStationId === undefined) return fallback;
    return graph.depthByFlowStationId.get(task.flowStationId) ?? fallback;
  };

  for (const line of byLine.values()) {
    line.chips.sort((a, b) => depthOf(a.taskId, 0) - depthOf(b.taskId, 0));
  }

  return [...byLine.values()];
}

export function selectItemTotals(tasks: KdsTaskDetail[]): ItemTotal[] {
  const totals = new Map<string, number>();
  for (const task of tasks) {
    if (!isActiveStatus(task.status)) continue;
    for (const item of task.items ?? []) {
      const name = item.itemName ?? 'Unknown item';
      totals.set(name, (totals.get(name) ?? 0) + (item.quantity ?? 1));
    }
  }
  return [...totals.entries()]
    .map(([name, quantity]) => ({ name, quantity }))
    .sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name));
}

export function selectRecentlyDone(tasks: KdsTaskDetail[], stationId: number): KdsTaskDetail[] {
  return tasks
    .filter((task) => task.status === 'DONE' && task.stationId === stationId)
    .sort((a, b) => updatedAtMs(b) - updatedAtMs(a))
    .slice(0, RECENTLY_DONE_CAP);
}

export function taskSummaryLine(task: KdsTaskDetail): string {
  return (task.items ?? [])
    .map((item) => {
      const qty = item.quantity ?? 1;
      const name = item.itemName ?? 'Unknown item';
      return qty > 1 ? `${qty}× ${name}` : name;
    })
    .join(', ');
}
